import { flower, sprite } from '../kit';
import { C, type Pix } from '../pix';
import { registerIcons } from '../store';
import { formedBlade, gem, R, ramp, tassel } from './craft';

/** Hand-made item pictures: hidden weapons (อาวุธลับ) and treasures (สมบัติ). */

const plum = ramp('#e85a7a');
const pearl = ramp('#e8eef0');
const stone = ramp('#6a6a7a');
const honeyJade = ramp('#d8a44a');

/** A throwing dart (biao) from tail (x0,y0) to point (x1,y1), with a short cloth tail. */
function dart(p: Pix, x0: number, y0: number, x1: number, y1: number, tail = R.red): void {
  formedBlade(p, x0, y0, x1, y1, R.iron, { w: 2.6, taper: 0.25, ridge: false });
  p.ring(x0, y0, 1.4, 1.4, R.iron.d1);
  const dx = Math.sign(x0 - x1);
  const dy = Math.sign(y0 - y1);
  p.line(x0, y0, x0 + dx * 4, y0 + dy * 2, tail.b);
  p.line(x0, y0, x0 + dx * 2, y0 + dy * 4, tail.d1);
}

/** A thin needle with a glint at its point. */
function needleAt(p: Pix, x0: number, y0: number, x1: number, y1: number, col = R.steel): void {
  p.line(x0, y0, x1, y1, col.b);
  p.px(x1, y1, col.l2);
  p.px(x0, y0, col.d1);
}

registerIcons('item', {
  // ลูกดอกเหล็กเจ็ดดอก: a fan of iron darts, red tails.
  'iron-darts': (p) => {
    dart(p, 8, 27, 20, 6);
    dart(p, 6, 24, 27, 13);
    dart(p, 11, 29, 28, 22);
  },
  // เข็มดอกเหมย: a bundle of fine needles over a plum blossom.
  'plum-needles': (p) => {
    flower(p, 11, 21, 6, plum.b, R.gold.l1);
    p.px(9, 19, plum.l2).px(13, 18, plum.l1);
    for (let k = 0; k < 5; k++) needleAt(p, 9 + k, 26 - k * 2 + k, 27 - k, 5 + k * 2);
  },
  // เข็มเงาเหมยแดง (legend): blood-red needles with their red shadows, a falling plum petal.
  'u-plum-needles': (p) => {
    for (let k = 0; k < 4; k++) needleAt(p, 4 + k * 2, 27 - k, 22 + k, 6 + k * 3, ramp('#8a2a5a'));
    for (let k = 0; k < 4; k++) needleAt(p, 6 + k * 2, 25 - k, 25 + k, 4 + k * 3, R.red);
    flower(p, 24, 22, 5, R.red.b, R.gold.l2);
    p.px(22, 20, R.red.l2);
    sprite(p, 6, 6, ['.r.', 'rRr', '.r.'], { r: R.red.d1, R: R.red.l1 });
  },
  // หยกพกพิทักษ์กาย: a jade bi disc on a red cord.
  'jade-amulet': (p) => {
    p.line(16, 2, 16, 6, R.red.b);
    p.ell(16, 17, 10, 10, R.jade.d1);
    p.ell(17, 16, 8.5, 8.5, R.jade.b);
    p.ring(16, 17, 9, 9, R.jade.l2, -1.5, -0.2);
    // Carved ring of grain bosses.
    for (let k = 0; k < 10; k++) {
      const a = (k * Math.PI) / 5;
      p.px(16 + Math.cos(a) * 6.5, 17 + Math.sin(a) * 6.5, k % 2 ? R.jade.l1 : R.jade.d1);
    }
    for (let y = 13; y < 21; y++) for (let x = 12; x < 20; x++) if (Math.hypot(x + 0.5 - 16, y + 0.5 - 17) < 3) p.erase(x, y);
    p.rect(14, 5, 4, 3, R.gold.b);
    p.px(17, 5, R.gold.l2);
    tassel(p, 16, 27, 2);
  },
  // ป้ายหยกเสือ: half of a tiger tally: a crouching honey-jade tiger in profile, head up, ears pricked,
  // dark stripes across its back, tail curled behind, a gold-inlaid inscription along its spine.
  'tiger-tally': (p) => {
    sprite(
      p,
      1,
      9,
      [
        '.....................DD...DD..',
        '....................DBBD.DBBD.',
        '.tt.................DBBBDBBBBD',
        't..t...............DBsBBBBsBBD',
        't...t..............DBBBBBBeBBD',
        '.t..t..ggggggggggg.DsBBBBBBBBn',
        '..tt.ddBBBBBBBBBBBddBBBBBBBBmm',
        '.....dbsbbbsbbbsbbbbsbbbbbbwm.',
        '....dbbsbbbsbbbsbbbbsbbbbbbb..',
        '....dbbbsbbbsbbbsbbbbbbbbbbd..',
        '....dbbbbbbbbbbbbbbbbbbbbbbd..',
        '....ddDDbbDDDDDDDDDbbbDDDbbD..',
        '...dbbbbbd..........dbbbbbbb..',
        '..dbbbbbbb.........dbbbbbbbbb.',
        '..DDDDDDDD.........DDDDDDDDDD.',
      ],
      {
        t: honeyJade.b,
        b: honeyJade.b,
        B: honeyJade.l1,
        d: honeyJade.d1,
        D: honeyJade.d2,
        s: '#4a2a1a',
        g: R.gold.l2,
        e: R.ink.b,
        n: honeyJade.d2,
        m: honeyJade.d1,
        w: C.white,
      },
    );
    // Glints on the brow and the haunch.
    p.px(25, 11, honeyJade.l2).px(8, 15, honeyJade.l1);
  },
  // พู่กระบี่สีแดง: a large sword tassel: jade bead, knot, red strands.
  'red-sword-tassel': (p) => {
    p.line(16, 2, 16, 6, R.gold.d1);
    gem(p, 16, 9, 3, R.jade);
    // Knot.
    sprite(p, 12, 12, ['.rrrrrrr.', 'rRrrrrrRr', '.rrrrrrr.'], { r: R.red.b, R: R.red.l1 });
    p.rect(13, 15, 7, 2, R.gold.b);
    p.px(18, 15, R.gold.l2);
    // Strands in three tone clusters, flaring at the ends.
    for (let k = -3; k <= 3; k++) {
      const c = k < -1 ? R.red.d1 : k > 1 ? R.red.l1 : R.red.b;
      p.line(16 + k, 17, 16 + k * 1.6, 29, c);
    }
    p.line(16, 17, 16, 29, R.red.d2);
  },
  // ไข่มุกสงบคลื่น (legend): a great pearl resting on a curled still wave.
  'dg-calm-pearl': (p) => {
    sprite(
      p,
      3,
      19,
      ['....bbbbb..........', '..bbBBBbbbb....bb..', '.bbB...bbbbbbbbbbb.', 'bbbbbbbbbbbbbbbbbbbb', '.dddddddddddddddddd.'],
      { b: R.azure.b, B: R.azure.l2, d: R.azure.d1 },
    );
    p.ell(17, 13, 7.5, 7.5, pearl.d1);
    p.ell(18, 12, 6, 6, pearl.b);
    p.ell(20, 10, 2.4, 2.4, pearl.l2);
    p.px(20, 9, C.white);
    p.ring(17, 13, 7, 7, ramp('#e0b8d8').b, 1.6, 2.8);
  },
  // ศิลาสงบอสนี (legend): a dark stone with a frozen lightning vein, quieted by blue runes.
  'treasure-wb-calm-thunder-stone': (p) => {
    p.poly(
      [
        [6, 22],
        [9, 9],
        [17, 4],
        [26, 9],
        [27, 21],
        [19, 28],
        [10, 27],
      ],
      stone.d1,
    );
    p.poly(
      [
        [10, 10],
        [17, 6],
        [25, 10],
        [25, 19],
        [16, 15],
      ],
      stone.b,
    );
    p.line(17, 5, 25, 9, stone.l1);
    // The lightning vein, gold, held still.
    p.path(
      [
        [13, 8],
        [17, 14],
        [13, 17],
        [19, 25],
      ],
      R.gold.l1,
    );
    p.px(17, 14, R.gold.l2);
    // Rune ticks round it.
    for (const [x, y] of [
      [9, 18],
      [22, 22],
      [23, 13],
    ] as const)
      sprite(p, x - 1, y - 1, ['.a.', 'aAa', '.a.'], { a: R.azure.b, A: R.azure.l2 });
  },
});
